/**
 * EmailJournalExportButton — the Email Validation tab's "Export PDF" control
 * (ADR-074 §3.5 req 8, Stage 6). Owned by the Diagnostics page and passed into
 * EmailValidationOutbound via its `renderExport` prop, so the surface stays a
 * pure renderer and the PDF/jsPDF dependency lives at the page level.
 *
 * The export reflects the operator's CURRENT view — it receives the already
 * filtered/searched/sorted rows. On failure it surfaces a friendly toast
 * (never a raw jsPDF error — nexa-prod error-surfacing standard,
 * frontend-behaviour-lessons #4/#8), and the failure is diagnostic-only here
 * (a client-side render fault, not an email send, so no Email alert is raised).
 */
import { useToast, toastFromError } from './ToastProvider';
import { buildEmailJournalPdf } from '../lib/email-journal-pdf';
import type { OutboundEmailRow } from '../lib/email-validation-api';
import { formatDate, todayIn } from '../lib/time-format';

/** Full day date, never month-only (universal rule). e.g. "24 June 2026". */
function todayLabel(): string {
  return formatDate(Date.now(), 'full');
}

/** Filename-safe yyyy-mm-dd for the saved file. */
function todayStamp(): string {
  return todayIn();
}

export function EmailJournalExportButton({ rows }: { rows: OutboundEmailRow[] }) {
  const toast = useToast();

  const onExport = () => {
    if (rows.length === 0) {
      toast.push({
        variant: 'info',
        title: 'Nothing to export',
        detail: 'No email journeys match the current filter or search.',
      });
      return;
    }
    try {
      const scope = `${rows.length} visible journey${rows.length === 1 ? '' : 's'} (current filter, search and sort)`;
      const doc = buildEmailJournalPdf(rows, scope, todayLabel());
      doc.save(`nexa-email-journal-${todayStamp()}.pdf`);
      toast.push({
        variant: 'success',
        title: 'Email journal exported',
        detail: `${rows.length} journey${rows.length === 1 ? '' : 's'} written to PDF.`,
      });
    } catch (err) {
      const t = toastFromError(err, 'Could not export the email journal');
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
    }
  };

  return (
    <button type="button" className="mon-btn mon-btn-sm" onClick={onExport}>
      Export PDF
    </button>
  );
}
